import { createFileRoute } from "@tanstack/react-router";
import { CostPageLayout } from "@/components/cost/CostPageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/commercial-roofing-cost-phoenix";
const TITLE = "Commercial Roofing Cost in Phoenix (2026) | Durafoam Roofing";
const DESCRIPTION =
  "Per-square-foot pricing for commercial foam, TPO, mod-bit, and built-up roofing in Phoenix. What drives the number for property managers and HOAs.";

const faqs = [
  { q: "How much does commercial roofing cost in Phoenix?", a: "Foam systems: $5.00–$9.00/sq ft installed for a standard 1\" foam build (tear-off additional if needed). TPO single-ply: $6.00–$10.00/sq ft. Mod-bit: $5.00–$8.00/sq ft. Recoats on existing commercial roofs: $1.40–$2.50/sq ft." },
  { q: "Do you work with property managers and HOAs?", a: "Yes — multi-property, multi-building, and HOA portfolio work is a core part of what we do. We schedule around tenant needs and document everything." },
  { q: "What system is best for commercial in Phoenix?", a: "For most low-slope commercial in the Valley, SPF foam wins on insulation value, seamless detailing around HVAC, and recoatability. TPO is competitive when a mechanical fastening system is required." },
];

export const Route = createFileRoute("/commercial-roofing-cost-phoenix")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Commercial Roofing",
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <CostPageLayout
      pageSlug="commercial-roofing-cost-phoenix"
      eyebrow="Phoenix cost guide"
      h1="Commercial Roofing Cost in Phoenix"
      subhead="2026 per-square-foot pricing for commercial roof systems across the Phoenix Valley — foam, TPO, mod-bit, and recoats."
      heroBullets={[
        "Foam install: $5.00–$9.00 per sq ft (1\" foam)",
        "Single-ply TPO: $6.00–$10.00 per sq ft",
        "Recoat: $1.40–$2.50 per sq ft",
        "Volume pricing on portfolio work",
      ]}
      intro={
        <>
          <p>
            Commercial roofing in Phoenix is priced per square foot, with the number driven by
            system type, deck condition, HVAC density, and access. SPF foam typically lands at{" "}
            <strong>$5.00–$9.00 per square foot installed</strong> for a standard 1" foam build
            (thicker available when R-value or substrate calls for it; tear-off additional if
            required); single-ply TPO at <strong>$6.00–$10.00</strong>; mod-bit at{" "}
            <strong>$5.00–$8.00</strong>.
          </p>
          <p>
            Recoats on an existing serviceable commercial roof are the highest-ROI move you can
            make: at a typical Phoenix cost of <strong>$1.00–$2.50 per sq ft</strong>, a recoat
            resets the roof's clock for another 5 years.
          </p>
        </>
      }
      pricingRows={[
        { label: "SPF foam — full install", range: "$5.00–$9.00 / sq ft", notes: "1\" foam + 2-coat elastomeric" },
        { label: "TPO single-ply", range: "$6.00–$10.00 / sq ft", notes: "Mechanically attached" },
        { label: "Modified bitumen (mod-bit)", range: "$5.00–$8.00 / sq ft", notes: "Torch or self-adhered" },
        { label: "Commercial recoat", range: "$1.40–$2.50 / sq ft", notes: "Highest ROI maintenance" },
        { label: "Tear-off (when required)", range: "+$0.80–$2.00 / sq ft", notes: "Disposal included" },
      ]}
      factors={[
        { title: "Building size", body: "Larger roofs are cheaper per square foot. Volume discounts kick in above ~10,000 sq ft." },
        { title: "HVAC density", body: "Rooftop AC, exhaust fans, gas lines, and pipe penetrations all need hand-detailed flashings." },
        { title: "Deck condition", body: "Wet insulation, deteriorated decking, or moisture in the existing system require tear-off and replacement." },
        { title: "Scheduling constraints", body: "After-hours, weekend, or phased work to keep tenants operational adds cost — but we plan around it." },
      ]}
      faqs={faqs}
      ctaTitle="Get a commercial roofing quote"
      ctaBody="Use the calculator for a quick estimate, or contact us for a property walk and a formal proposal."
    />
  );
}
